<template>
  <form id="admin-panel">
    <h2>Warhall Admin Config Panel</h2>

    <!-- ── Ban Skins ─────────────────────────────────────── -->
    <section>
      <h3>Ban Skins</h3>
      <p class="hint">Enter a comma-separated list of Skin IDs to ban.</p>

      <label>Skin IDs</label>
      <textarea
        v-model="rawInput"
        placeholder="e.g. 42, 107, 305"
        rows="4"
        :disabled="loading"
      />

      <label class="checkbox-label">
        <input type="checkbox" v-model="deleteAssets" :disabled="loading" />
        Also delete assets from server (thumbnail + model file)
      </label>

      <button @click.prevent="submit" :disabled="loading || !rawInput.trim()">
        {{ loading ? 'Processing…' : 'Ban skins' }}
      </button>

      <div v-if="results.length" class="results">
        <div
          v-for="r in results"
          :key="r.id"
          :class="['result-row', r.ok ? 'ok' : 'err']"
        >
          <span class="skin-id">ID {{ r.id }}</span>
          <span class="status">{{ r.summary }}</span>
        </div>
      </div>
    </section>

    <!-- ── Ban Descriptions ───────────────────────────────── -->
    <section>
      <h3>Ban Descriptions</h3>
      <p class="hint">
        Flag a WDS item description as copyrighted. Enter the WDS string ID and the Game ID.<br>
        For NR rules use the rule string ID (e.g. <code>magical_attacks</code>).<br>
        For Spells use <code>spell_N</code> (e.g. <code>spell_42</code>).
      </p>

      <label>WDS / Rule / Spell ID</label>
      <input v-model="descWdsId" placeholder="e.g. magical_attacks" :disabled="descLoading" />

      <label>Game ID</label>
      <input v-model.number="descGameId" type="number" placeholder="e.g. 1" :disabled="descLoading" />

      <button @click.prevent="addBannedDescription" :disabled="descLoading || !descWdsId.trim() || !descGameId">
        {{ descLoading ? 'Processing…' : 'Ban description' }}
      </button>

      <div v-if="descResults.length" class="results">
        <div
          v-for="r in descResults"
          :key="r.key"
          :class="['result-row', r.ok ? 'ok' : 'err']"
        >
          <span class="skin-id">{{ r.key }}</span>
          <span class="status">{{ r.summary }}</span>
        </div>
      </div>

      <hr style="margin: 4px 0;" />

      <div style="display:flex; gap:8px; align-items:flex-end;">
        <div style="flex:1;">
          <label>Load list for Game ID</label>
          <input v-model.number="descListGameId" type="number" placeholder="e.g. 1" :disabled="descLoading" />
        </div>
        <button @click.prevent="loadBannedDescriptions" :disabled="descLoading || !descListGameId" style="margin-bottom:0;">
          Load
        </button>
      </div>

      <table v-if="descBanList.length" style="width:100%; font-size:0.85rem; border-collapse:collapse;">
        <thead>
          <tr>
            <th style="text-align:left; padding:4px 8px;">WDS ID</th>
            <th style="text-align:left; padding:4px 8px;">Game ID</th>
            <th style="padding:4px 8px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ban in descBanList" :key="ban.wdsId">
            <td style="padding:4px 8px;">{{ ban.wdsId }}</td>
            <td style="padding:4px 8px;">{{ descListGameId }}</td>
            <td style="padding:4px 8px;">
              <button @click.prevent="removeBannedDescription(ban.wdsId, descListGameId)" :disabled="descLoading">Remove</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else-if="descBanList !== null && descListGameId" class="hint">No banned descriptions for this game.</p>
    </section>

    <!-- ── Ban URLs ───────────────────────────────────────── -->
    <section>
      <h3>Ban URLs</h3>
      <p class="hint">
        Flag a card's image URL, rule icon, or faction icon as banned.<br>
        • <strong>Card</strong>: Enter the card's WDS ID (e.g. <code>spell_card_42</code>). The card will display its solid colour instead of loading the image.<br>
        • <strong>Rule Icon</strong>: Enter the rule's WDS ID (e.g. <code>rulebook-general</code>). The soldier gizmo icon will not appear.<br>
        • <strong>Faction Icon</strong>: Enter the army name (e.g. <code>Human Alliance</code>). The dropdown will show text only, no icon.
      </p>

      <label>WDS ID / Identifier</label>
      <input v-model="urlWdsId" placeholder="e.g. spell_card_42, rulebook-general, Human Alliance" :disabled="urlLoading" />

      <label>Game ID</label>
      <input v-model.number="urlGameId" type="number" placeholder="e.g. 1" :disabled="urlLoading" />

      <button @click.prevent="addBannedUrl" :disabled="urlLoading || !urlWdsId.trim() || !urlGameId">
        {{ urlLoading ? 'Processing…' : 'Ban' }}
      </button>

      <div v-if="urlResults.length" class="results">
        <div
          v-for="r in urlResults"
          :key="r.key"
          :class="['result-row', r.ok ? 'ok' : 'err']"
        >
          <span class="skin-id">{{ r.key }}</span>
          <span class="status">{{ r.summary }}</span>
        </div>
      </div>

      <hr style="margin: 4px 0;" />

      <div style="display:flex; gap:8px; align-items:flex-end;">
        <div style="flex:1;">
          <label>Load list for Game ID</label>
          <input v-model.number="urlListGameId" type="number" placeholder="e.g. 1" :disabled="urlLoading" />
        </div>
        <button @click.prevent="loadBannedUrls" :disabled="urlLoading || !urlListGameId" style="margin-bottom:0;">
          Load
        </button>
      </div>

      <table v-if="urlBanList.length" style="width:100%; font-size:0.85rem; border-collapse:collapse;">
        <thead>
          <tr>
            <th style="text-align:left; padding:4px 8px;">WDS ID / Identifier</th>
            <th style="text-align:left; padding:4px 8px;">Game ID</th>
            <th style="padding:4px 8px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ban in urlBanList" :key="ban.wdsId">
            <td style="padding:4px 8px;">{{ ban.wdsId }}</td>
            <td style="padding:4px 8px;">{{ urlListGameId }}</td>
            <td style="padding:4px 8px;">
              <button @click.prevent="removeBannedUrl(ban.wdsId, urlListGameId)" :disabled="urlLoading">Remove</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else-if="urlBanList !== null && urlListGameId" class="hint">No banned items for this game.</p>
    </section>

    <!-- ── Word Filter ────────────────────────────────────── -->
    <section>
      <h3>Word Filter</h3>
      <p class="hint">
        Add or remove text replacement rules. <strong>Key</strong> is the string to match;
        <strong>Value</strong> is what it is replaced with in the Unity client.<br>
        Changes take effect for Unity clients on their next session start.
      </p>

      <div style="display:flex; gap:8px;">
        <div style="flex:1;">
          <label>Key (string to replace)</label>
          <input v-model="wfKey" placeholder="e.g. Warhammer" :disabled="wfLoading" />
        </div>
        <div style="flex:1;">
          <label>Value (replacement)</label>
          <input v-model="wfValue" placeholder="e.g. Wargame" :disabled="wfLoading" />
        </div>
      </div>

      <button @click.prevent="addWordFilter" :disabled="wfLoading || !wfKey.trim()">
        {{ wfLoading ? 'Processing…' : 'Add / update entry' }}
      </button>

      <div v-if="wfResults.length" class="results">
        <div
          v-for="r in wfResults"
          :key="r.key"
          :class="['result-row', r.ok ? 'ok' : 'err']"
        >
          <span class="skin-id">{{ r.key }}</span>
          <span class="status">{{ r.summary }}</span>
        </div>
      </div>

      <hr style="margin: 4px 0;" />

      <button @click.prevent="loadWordFilter" :disabled="wfLoading" style="align-self:flex-start;">
        {{ wfLoading ? 'Loading…' : 'Load current list' }}
      </button>

      <table v-if="wfList.length" style="width:100%; font-size:0.85rem; border-collapse:collapse;">
        <thead>
          <tr>
            <th style="text-align:left; padding:4px 8px;">Key</th>
            <th style="text-align:left; padding:4px 8px;">Value</th>
            <th style="padding:4px 8px;"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in wfList" :key="entry.key">
            <td style="padding:4px 8px;">{{ entry.key }}</td>
            <td style="padding:4px 8px;">{{ entry.value }}</td>
            <td style="padding:4px 8px;">
              <button @click.prevent="removeWordFilter(entry.key)" :disabled="wfLoading">Remove</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else-if="wfList !== null && wfList.length === 0 && wfListLoaded" class="hint">No word filter entries.</p>
    </section>

    <router-link :to="{ name: 'userhome' }">Back</router-link>
  </form>
</template>

<script>
export default {
  name: 'WarhallAdminConfigPanel',

  data() {
    return {
      // Ban Skins
      rawInput: '',
      deleteAssets: false,
      loading: false,
      results: [],

      // Ban Descriptions
      descWdsId: '',
      descGameId: null,
      descLoading: false,
      descResults: [],
      descBanList: [],
      descListGameId: null,

      // Ban URLs
      urlWdsId: '',
      urlGameId: null,
      urlLoading: false,
      urlResults: [],
      urlBanList: [],
      urlListGameId: null,

      // Word Filter
      wfKey: '',
      wfValue: '',
      wfLoading: false,
      wfResults: [],
      wfList: [],
      wfListLoaded: false,
    };
  },

  methods: {
    async submit() {
      this.results = [];

      const ids = [
        ...new Set(
          this.rawInput
            .split(',')
            .map(s => parseInt(s.trim(), 10))
            .filter(n => Number.isInteger(n) && n > 0)
        ),
      ];

      if (ids.length === 0) {
        this.results = [{ id: '—', ok: false, summary: 'No valid IDs found.' }];
        return;
      }

      this.loading = true;

      for (const skinId of ids) {
        let ok = false;
        let summary = '';

        // 1. Ban the skin
        try {
          const banRes = await fetch('/BanSkin', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ skinId }),
          });
          const banJson = await banRes.json();

          if (banJson.error !== 0) {
            summary = `Ban failed: ${banJson.result || 'error ' + banJson.error}`;
            this.results.push({ id: skinId, ok: false, summary });
            continue;
          }

          ok = true;
          summary = '✓ banned';
        } catch (err) {
          summary = `Ban error: ${String(err)}`;
          this.results.push({ id: skinId, ok: false, summary });
          continue;
        }

        // 2. Optionally delete S3 assets
        if (this.deleteAssets) {
          try {
            const delRes = await fetch('/DeleteSkinAssets', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ skinId }),
            });
            const delJson = await delRes.json();

            if (delJson.error === 0) {
              const nDeleted = delJson.deleted ? delJson.deleted.length : 0;
              const nFailed  = delJson.failed  ? delJson.failed.length  : 0;
              summary += `, ${nDeleted} asset(s) deleted`;
              if (nFailed > 0) summary += ` (${nFailed} failed)`;
            } else {
              ok = false;
              summary += ` — asset deletion failed: ${delJson.result || 'error ' + delJson.error}`;
            }
          } catch (err) {
            ok = false;
            summary += ` — asset deletion error: ${String(err)}`;
          }
        }

        this.results.push({ id: skinId, ok, summary });
      }

      this.loading = false;
    },

    async loadBannedDescriptions() {
      if (!this.descListGameId) return;
      this.descLoading = true;
      try {
        const res = await fetch(`/GetBannedDescriptions/${this.descListGameId}`, { credentials: 'include' });
        const json = await res.json();
        this.descBanList = json.items || [];
      } catch (err) {
        this.descResults = [{ key: '—', ok: false, summary: `Load error: ${String(err)}` }];
      } finally {
        this.descLoading = false;
      }
    },

    async addBannedDescription() {
      const wdsId = this.descWdsId.trim();
      const gameId = this.descGameId;
      if (!wdsId || !gameId) return;
      this.descLoading = true;
      this.descResults = [];
      try {
        const res = await fetch('/AddBannedDescription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ wdsId, gameId }),
        });
        const json = await res.json();
        if (json.error === 0) {
          this.descResults = [{ key: wdsId, ok: true, summary: '✓ banned' }];
          this.descWdsId = '';
          if (this.descListGameId === gameId) await this.loadBannedDescriptions();
        } else {
          this.descResults = [{ key: wdsId, ok: false, summary: json.result || `error ${json.error}` }];
        }
      } catch (err) {
        this.descResults = [{ key: wdsId, ok: false, summary: String(err) }];
      } finally {
        this.descLoading = false;
      }
    },

    async removeBannedDescription(wdsId, gameId) {
      this.descLoading = true;
      try {
        const res = await fetch('/RemoveBannedDescription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ wdsId, gameId }),
        });
        const json = await res.json();
        if (json.error === 0) {
          await this.loadBannedDescriptions();
        } else {
          this.descResults = [{ key: wdsId, ok: false, summary: json.result || `error ${json.error}` }];
        }
      } catch (err) {
        this.descResults = [{ key: wdsId, ok: false, summary: String(err) }];
      } finally {
        this.descLoading = false;
      }
    },

    async loadBannedUrls() {
      if (!this.urlListGameId) return;
      this.urlLoading = true;
      try {
        const res = await fetch(`/GetBannedUrls/${this.urlListGameId}`, { credentials: 'include' });
        const json = await res.json();
        this.urlBanList = json.items || [];
      } catch (err) {
        this.urlResults = [{ key: '—', ok: false, summary: `Load error: ${String(err)}` }];
      } finally {
        this.urlLoading = false;
      }
    },

    async addBannedUrl() {
      const wdsId = this.urlWdsId.trim();
      const gameId = this.urlGameId;
      if (!wdsId || !gameId) return;
      this.urlLoading = true;
      this.urlResults = [];
      try {
        const res = await fetch('/AddBannedUrl', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ wdsId, gameId }),
        });
        const json = await res.json();
        if (json.error === 0) {
          this.urlResults = [{ key: wdsId, ok: true, summary: '✓ banned' }];
          this.urlWdsId = '';
          if (this.urlListGameId === gameId) await this.loadBannedUrls();
        } else {
          this.urlResults = [{ key: wdsId, ok: false, summary: json.result || `error ${json.error}` }];
        }
      } catch (err) {
        this.urlResults = [{ key: wdsId, ok: false, summary: String(err) }];
      } finally {
        this.urlLoading = false;
      }
    },

    async removeBannedUrl(wdsId, gameId) {
      this.urlLoading = true;
      try {
        const res = await fetch('/RemoveBannedUrl', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ wdsId, gameId }),
        });
        const json = await res.json();
        if (json.error === 0) {
          await this.loadBannedUrls();
        } else {
          this.urlResults = [{ key: wdsId, ok: false, summary: json.result || `error ${json.error}` }];
        }
      } catch (err) {
        this.urlResults = [{ key: wdsId, ok: false, summary: String(err) }];
      } finally {
        this.urlLoading = false;
      }
    },

    async loadWordFilter() {
      this.wfLoading = true;
      try {
        const res = await fetch('/GetWordFilterList', { credentials: 'include' });
        this.wfList = await res.json();
        this.wfListLoaded = true;
      } catch (err) {
        this.wfResults = [{ key: '—', ok: false, summary: `Load error: ${String(err)}` }];
      } finally {
        this.wfLoading = false;
      }
    },

    async addWordFilter() {
      const key = this.wfKey.trim();
      const value = this.wfValue;
      if (!key) return;
      this.wfLoading = true;
      this.wfResults = [];
      try {
        const res = await fetch('/AddWordFilter', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ key, value }),
        });
        const json = await res.json();
        if (json.error === 0) {
          this.wfResults = [{ key, ok: true, summary: '✓ saved' }];
          this.wfKey = '';
          this.wfValue = '';
          if (this.wfListLoaded) await this.loadWordFilter();
        } else {
          this.wfResults = [{ key, ok: false, summary: json.result || `error ${json.error}` }];
        }
      } catch (err) {
        this.wfResults = [{ key, ok: false, summary: String(err) }];
      } finally {
        this.wfLoading = false;
      }
    },

    async removeWordFilter(key) {
      this.wfLoading = true;
      try {
        const res = await fetch('/RemoveWordFilter', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ key }),
        });
        const json = await res.json();
        if (json.error === 0) {
          await this.loadWordFilter();
        } else {
          this.wfResults = [{ key, ok: false, summary: json.result || `error ${json.error}` }];
        }
      } catch (err) {
        this.wfResults = [{ key, ok: false, summary: String(err) }];
      } finally {
        this.wfLoading = false;
      }
    },
  },
};
</script>

<style scoped>
#admin-panel {
  max-width: 520px;
  margin: 60px auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px;
}

h2 { margin: 0 0 4px; }
h3 { margin: 0 0 6px; font-size: 1rem; }

section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.hint {
  margin: 0;
  font-size: 0.85rem;
  opacity: 0.7;
}

textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  font-size: 1rem;
  resize: vertical;
  color: black;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  cursor: pointer;
}

button {
  align-self: flex-start;
  padding: 8px 20px;
  cursor: pointer;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.results {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.result-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.9rem;
}

.result-row.ok  { background: #d4edda; color: #155724; }
.result-row.err { background: #f8d7da; color: #721c24; }

.skin-id { font-weight: bold; }
</style>
